import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { useAuth } from "@/auth/AuthProvider";
import { api } from "@/lib/api";

type VtpAccount = {
  id: number;
  label: string;
  active: boolean;
  note: string | null;
  username_masked: string;
  last_login_at: string | null;
  last_login_token_expires_at: string | null;
};

type TestResult = {
  ok: boolean;
  token_preview?: string;
  expired_at?: number;
  user_id?: number;
  partner?: number;
  error?: string;
};

const fmtDateTime = (s: string | null) => (s ? new Date(s).toLocaleString("vi-VN") : "—");

export function VtpAccountsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const qc = useQueryClient();
  const [editing, setEditing] = useState<VtpAccount | "new" | null>(null);
  const [testResult, setTestResult] = useState<{ accountId: number; r: TestResult } | null>(
    null
  );

  const listQ = useQuery({
    queryKey: ["vtp-accounts"],
    queryFn: async () => (await api.get<VtpAccount[]>("/vtp-accounts")).data,
  });

  const testMut = useMutation({
    mutationFn: async (id: number) =>
      (await api.post<TestResult>(`/vtp-accounts/${id}/test`)).data,
    onSuccess: (r, id) => {
      setTestResult({ accountId: id, r });
      qc.invalidateQueries({ queryKey: ["vtp-accounts"] });
    },
    onError: (e: any, id) =>
      setTestResult({
        accountId: id,
        r: { ok: false, error: e?.response?.data?.detail || String(e) },
      }),
  });

  const delMut = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/vtp-accounts/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["vtp-accounts"] }),
  });

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-xl font-semibold text-slate-900">Tài khoản Viettel Post</h1>
        {isAdmin && (
          <button
            onClick={() => setEditing("new")}
            className="btn-primary"
          >
            + Thêm tài khoản
          </button>
        )}
      </div>
      <div className="text-sm text-gray-600 mb-4">
        Dùng chung cho tất cả shop. Khi POScake gửi webhook đơn có VTP code,
        hệ thống sẽ lookup chi tiết VTP qua account đầu tiên đang active.
        Có thể thêm nhiều account để failover.
      </div>

      <table className="w-full bg-white border border-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="text-left px-3 py-2">Nhãn</th>
            <th className="text-left px-3 py-2">Username</th>
            <th className="text-left px-3 py-2">Active</th>
            <th className="text-left px-3 py-2">Login gần nhất</th>
            <th className="text-left px-3 py-2">Token hết hạn</th>
            <th className="text-right px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {listQ.isLoading && (
            <tr>
              <td colSpan={6} className="px-3 py-6 text-center text-gray-500">
                Đang tải...
              </td>
            </tr>
          )}
          {listQ.data?.length === 0 && (
            <tr>
              <td colSpan={6} className="px-3 py-6 text-center text-gray-500">
                Chưa có tài khoản VTP. Bấm "+ Thêm tài khoản" để bắt đầu.
              </td>
            </tr>
          )}
          {listQ.data?.map((a) => (
            <tr key={a.id} className="border-t border-gray-100">
              <td className="px-3 py-2 font-medium">{a.label}</td>
              <td className="px-3 py-2 font-mono text-xs">{a.username_masked}</td>
              <td className="px-3 py-2">{a.active ? "✓" : "—"}</td>
              <td className="px-3 py-2 text-xs text-gray-600">
                {fmtDateTime(a.last_login_at)}
              </td>
              <td className="px-3 py-2 text-xs text-gray-600">
                {fmtDateTime(a.last_login_token_expires_at)}
              </td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                {isAdmin && (
                  <>
                    <button
                      onClick={() => testMut.mutate(a.id)}
                      disabled={testMut.isPending}
                      className="text-xs text-gray-600 hover:text-gray-900 mr-2"
                    >
                      Test login
                    </button>
                    <button
                      onClick={() => setEditing(a)}
                      className="text-blue-600 hover:underline text-sm mr-2"
                    >
                      Sửa
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Xoá tài khoản "${a.label}"?`)) delMut.mutate(a.id);
                      }}
                      className="text-red-600 hover:underline text-sm"
                    >
                      Xoá
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {testResult && (
        <div
          className={`mt-3 border rounded p-3 text-sm ${
            testResult.r.ok
              ? "bg-green-50 border-green-300 text-green-800"
              : "bg-red-50 border-red-300 text-red-800"
          }`}
        >
          <div className="font-medium mb-1">
            {testResult.r.ok ? "✓" : "✗"} Test login account #{testResult.accountId}
          </div>
          <pre className="text-xs whitespace-pre-wrap">
            {JSON.stringify(testResult.r, null, 2)}
          </pre>
        </div>
      )}

      {editing && (
        <EditDrawer
          account={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["vtp-accounts"] });
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function EditDrawer({
  account,
  onClose,
  onSaved,
}: {
  account: VtpAccount | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = account === null;
  const [form, setForm] = useState({
    label: account?.label ?? "",
    username: "",
    password: "",
    active: account?.active ?? true,
    note: account?.note ?? "",
  });
  useEffect(() => {
    setForm({
      label: account?.label ?? "",
      username: "",
      password: "",
      active: account?.active ?? true,
      note: account?.note ?? "",
    });
  }, [account]);

  const save = useMutation({
    mutationFn: async () => {
      if (isNew) {
        return (await api.post<VtpAccount>("/vtp-accounts", form)).data;
      }
      const payload: any = {
        label: form.label,
        active: form.active,
        note: form.note,
      };
      if (form.username.trim()) payload.username = form.username.trim();
      if (form.password) payload.password = form.password;
      return (await api.patch<VtpAccount>(`/vtp-accounts/${account!.id}`, payload)).data;
    },
    onSuccess: onSaved,
  });

  return (
    <div className="fixed inset-0 bg-black/30 flex justify-end z-50">
      <div className="w-full max-w-lg bg-white h-full overflow-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">
            {isNew ? "Thêm tài khoản VTP" : `Sửa "${account?.label}"`}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900">
            ✕
          </button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
          className="space-y-3"
        >
          <div>
            <label className="block text-xs text-gray-600 mb-0.5">Nhãn *</label>
            <input
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              required
              placeholder="vd: Account chính, Tài khoản phụ ..."
              className="w-full border rounded px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-0.5">
              Username {isNew && "*"}
            </label>
            <input
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              required={isNew}
              placeholder={
                isNew
                  ? "Số điện thoại tài khoản portal VTP"
                  : `Hiện tại: ${account?.username_masked} (bỏ trống để giữ)`
              }
              className="w-full border rounded px-2 py-1.5 text-sm font-mono"
              autoComplete="off"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-0.5">
              Password {isNew && "*"}
            </label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required={isNew}
              placeholder={isNew ? "Mật khẩu portal" : "(không đổi)"}
              className="w-full border rounded px-2 py-1.5 text-sm"
              autoComplete="new-password"
            />
            <div className="text-xs text-gray-500 mt-0.5">
              Lưu mã hoá AES-256-GCM. Token VTP cache 360 ngày.
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-0.5">Ghi chú</label>
            <textarea
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              rows={2}
              className="w-full border rounded px-2 py-1.5 text-sm"
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            Đang hoạt động (chỉ account active mới dùng lookup)
          </label>

          {save.error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">
              {(save.error as any)?.response?.data?.detail ?? String(save.error)}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
            >
              Huỷ
            </button>
            <button
              type="submit"
              disabled={save.isPending}
              className="flex-1 px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-60"
            >
              {save.isPending ? "Đang lưu..." : "Lưu"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
